/**
 * Users guide — multi-tenancy with user_id for end-user data isolation.
 *
 * Covers setting user_id on all resources, list filtering, and strict
 * isolation guarantees for memories, history, and teammate reuse.
 */

export const usersContent = `
When building products on top of m8tes, use the \`user_id\` field to isolate each end-user's data. Teammates, runs, memories, and tasks are all scoped by \`user_id\`.

## Why user_id?

Without \`user_id\`, all resources belong to your account globally. With \`user_id\`, each end-user gets:

- **Isolated teammates** — users only see their own bots
- **Isolated memories** — teammate context is per-user
- **Isolated task history** — no cross-user data leakage
- **Scoped teammate reuse** — quick-start mode reuses teammates per user

## Set user_id on Everything

### Teammates

\`\`\`python
from m8tes import M8tes

client = M8tes()

# Create a teammate for a specific end-user
bot = client.teammates.create(
    name="Support Bot",
    tools=["gmail"],
    user_id="cust_123"
)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/teammates \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Support Bot", "tools": ["gmail"], "user_id": "cust_123"}'
\`\`\`

### Runs

\`\`\`python
# Run scoped to the same end-user
for event in client.runs.create(
    teammate_id=bot.id,
    message="Close open tickets",
    user_id="cust_123"
):
    print(event.type, event.raw)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1, "message": "Close open tickets", "user_id": "cust_123", "stream": false}'
\`\`\`

### Quick Start (auto-create teammate)

\`\`\`python
# Omit teammate_id — auto-creates a teammate scoped to this user
run = client.runs.create(
    message="Draft weekly report",
    name="Report Bot",
    instructions="Write concise reports",
    user_id="cust_123",
    stream=False
)
\`\`\`

If \`cust_123\` runs this again with the same \`name\`, the existing teammate is reused.

### Memories

\`\`\`python
# Seed a memory for the user
client.memories.create(user_id="cust_123", content="Prefers email over Slack")
client.memories.create(user_id="cust_123", content="Timezone: America/New_York")
\`\`\`

### Tasks

\`\`\`python
task = client.tasks.create(
    teammate_id=bot.id,
    instructions="Weekly ticket summary",
    user_id="cust_123"
)
\`\`\`

## Filtering by user_id

All list endpoints accept \`user_id\` to return only that user's resources:

\`\`\`python
# Only cust_123's teammates
page = client.teammates.list(user_id="cust_123")
for t in page.data:
    print(t.name)

# Only cust_123's runs
page = client.runs.list(user_id="cust_123")

# Only cust_123's memories
page = client.memories.list(user_id="cust_123")

# Only cust_123's tasks (via teammate join)
page = client.tasks.list(user_id="cust_123")
\`\`\`

\`\`\`bash
# List only cust_123's runs
curl "https://m8tes.ai/api/v2/runs?user_id=cust_123" \\
  -H "Authorization: Bearer m8_your_key_here"

# List only cust_123's memories
curl "https://m8tes.ai/api/v2/memories?user_id=cust_123" \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

Without the filter, list endpoints return all resources across all end-users (for your account).

## Best Practices

1. **Always pass user_id consistently** — set it on teammates, runs, memories, and tasks
2. **Use stable IDs** — use your application's user IDs (e.g. \`usr_abc\`, database IDs)
3. **Don't mix scoped and unscoped** — if you use \`user_id\` for one user, use it for all
4. **Isolation is strict** — when \`user_id\` is set, only that user's data is visible to the teammate (memories, history, etc.)

## What's Next

- [Memories](/docs/memories) — personalize teammate context per end-user
- [Permissions](/docs/permissions) — control tool access per end-user
- [Tools](/docs/skills) — end-user OAuth connections
`;
